import React, { useState, useMemo } from 'react';
import { berechneBasisPct } from './Dashboard';
import { istEntwurf } from './utils/themeUtils';

// ─── Theme ────────────────────────────────────────────────────────────────────
const C = {
  bg:           '#F5F0E8',
  bgGrad:       'radial-gradient(130% 90% at 20% 0%, #FBF7ED 0%, #F5F0E8 55%, #EDE6D6 100%)',
  surface:      'rgba(27,23,15,0.04)',
  surfaceStrong:'rgba(27,23,15,0.07)',
  border:       'rgba(27,23,15,0.09)',
  borderStrong: 'rgba(27,23,15,0.16)',
  text:         '#1C1812',
  textMuted:    'rgba(28,24,18,0.58)',
  textDim:      'rgba(28,24,18,0.34)',
  accent:       '#C8860A',
  accentBg:     'rgba(200,134,10,0.12)',
  accentText:   '#FBF7ED',
  dark:         '#1A1208',
  darkCard:     '#221A08',
  mono:         "'Geist Mono', 'Courier New', monospace",
  head:         "'Playfair Display', Georgia, serif",
  body:         "'Geist', -apple-system, system-ui, sans-serif",
};

// ─── Icons ────────────────────────────────────────────────────────────────────
const DocIcon = () => (
  <svg width="20" height="24" viewBox="0 0 20 24" fill="none">
    <rect x="1" y="1" width="18" height="22" rx="3" fill="rgba(200,134,10,0.15)" stroke="rgba(200,134,10,0.4)" strokeWidth="1.2"/>
    <path d="M5 8h10M5 12h10M5 16h6" stroke="rgba(200,134,10,0.7)" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

const StarIcon = ({ filled }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill={filled ? C.accent : 'none'}>
    <path d="M7 1l1.5 4H13l-3.5 2.5 1.3 4L7 9.5 3.2 11.5l1.3-4L1 5h4.5L7 1z"
      stroke={filled ? C.accent : C.textDim} strokeWidth="1.2" strokeLinejoin="round"/>
  </svg>
);

const DotsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="3" cy="8" r="1.3" fill={C.textDim}/>
    <circle cx="8" cy="8" r="1.3" fill={C.textDim}/>
    <circle cx="13" cy="8" r="1.3" fill={C.textDim}/>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 2v12M2 8h12" stroke={C.accentText} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// ─── Datums-Formatierung ──────────────────────────────────────────────────────
function formatDatum(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now - d) / 86400000);
  if (diff === 0) return 'aktualisiert heute';
  if (diff === 1) return 'aktualisiert gestern';
  if (diff < 7)  return `vor ${diff} Tagen`;
  return d.toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' });
}

function countPages(stelle) {
  if (stelle.cv_data?.sektionen) return Math.max(1, Math.ceil(stelle.cv_data.sektionen.length / 3));
  if (stelle.cv_rohtext) return Math.max(1, Math.ceil(stelle.cv_rohtext.length / 1800));
  return 1;
}

// ─── Completion Card ──────────────────────────────────────────────────────────
function CompletionCard({ profile, stellen }) {
  const pct = berechneBasisPct(profile);
  const hatCV = stellen.some(s => s.cv_data || s.cv_rohtext);
  const hatAnschreiben = stellen.some(s => s.anschreiben || s.anschreiben_rohtext);

  const fehlend = [];
  if (!profile?.email?.trim()) fehlend.push('E-Mail');
  if (!profile?.telefon?.trim()) fehlend.push('Telefon');
  if (!hatCV) fehlend.push('Lebenslauf');
  if (!hatAnschreiben) fehlend.push('Anschreiben');

  const total = Math.min(100, Math.round(pct * 0.6 + (hatCV ? 25 : 0) + (hatAnschreiben ? 15 : 0)));

  return (
    <div style={{
      margin: '0 16px 20px',
      background: C.darkCard,
      borderRadius: 20,
      padding: '18px 20px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>
            Mappe vollständig
          </div>
          {fehlend.length > 0 && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.4 }}>
              Noch fehlt: {fehlend.slice(0, 3).join(' · ')}
            </div>
          )}
        </div>
        <div style={{ fontSize: 36, fontWeight: 800, color: C.accent, fontFamily: C.head, letterSpacing: -1, lineHeight: 1 }}>
          {total}%
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height: 6, borderRadius: 999, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 999,
          background: `linear-gradient(90deg, ${C.accent}, #F5C842)`,
          width: `${total}%`,
          transition: 'width 0.8s cubic-bezier(0.34,1.56,0.64,1)',
          boxShadow: `0 0 12px ${C.accent}66`,
        }} />
      </div>
    </div>
  );
}

// ─── File Card ────────────────────────────────────────────────────────────────
function FileCard({ stelle, starred, onStarToggle, onOpen }) {
  const pages = countPages(stelle);
  const date = formatDatum(stelle.generiert_am || stelle.erstellt_am);
  const isDefault = starred;

  return (
    <div
      onClick={onOpen}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 16, padding: '14px 16px',
        cursor: 'pointer',
        transition: 'background 0.15s',
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <DocIcon />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.text, letterSpacing: -0.2, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {stelle.name || 'Lebenslauf'}.pdf
        </div>
        <div style={{ fontSize: 11.5, color: C.textMuted, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>{pages} {pages === 1 ? 'Seite' : 'Seiten'}</span>
          {date && <><span style={{ opacity: 0.4 }}>·</span><span>{date}</span></>}
          {isDefault && <><span style={{ opacity: 0.4 }}>·</span><span style={{ color: C.accent }}>Standard</span></>}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <button
          onClick={e => { e.stopPropagation(); onStarToggle(); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, WebkitTapHighlightColor: 'transparent' }}
        >
          <StarIcon filled={starred} />
        </button>
        <button
          onClick={e => e.stopPropagation()}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, WebkitTapHighlightColor: 'transparent' }}
        >
          <DotsIcon />
        </button>
      </div>
    </div>
  );
}

// ─── Tab Bar ──────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'cv',          label: 'Lebensläufe'  },
  { id: 'anschreiben', label: 'Anschreiben'  },
  { id: 'dokumente',   label: 'Dokumente'    },
  { id: 'fotos',       label: 'Fotos'        },
];

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
export default function MobileProfil({ nutzer, aktivesProfile, stellen, onStelleOeffnen }) {
  const [tab, setTab] = useState('cv');
  const [starredId, setStarredId] = useState(null);

  const vorname  = aktivesProfile?.vorname  || nutzer?.displayName?.split(' ')[0] || '';
  const nachname = aktivesProfile?.nachname || nutzer?.displayName?.split(' ').slice(1).join(' ') || '';
  const fullName = [vorname, nachname].filter(Boolean).join(' ') || 'Mein Profil';
  const initialen = (vorname[0] || '') + (nachname[0] || '') || '?';
  const ort = aktivesProfile?.ort || '';

  const cvStellen = useMemo(() =>
    stellen.filter(s => s.cv_data || s.cv_rohtext)
      .sort((a,b) => new Date(b.generiert_am||b.erstellt_am||0) - new Date(a.generiert_am||a.erstellt_am||0)),
    [stellen]
  );

  const anschreibenStellen = useMemo(() =>
    stellen.filter(s => s.anschreiben || s.anschreiben_rohtext)
      .sort((a,b) => new Date(b.generiert_am||b.erstellt_am||0) - new Date(a.generiert_am||a.erstellt_am||0)),
    [stellen]
  );

  const defaultId = starredId ?? cvStellen[0]?.id ?? null;

  return (
    <div style={{
      position: 'absolute', inset: 0,
      background: C.bgGrad,
      fontFamily: C.body,
      WebkitFontSmoothing: 'antialiased',
      display: 'flex', flexDirection: 'column',
      color: C.text,
    }}>

      {/* ── Scrollbarer Hauptbereich ── */}
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>

        {/* ── Profil-Header ── */}
        <div style={{
          padding: '0 20px 20px',
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
            {/* Avatar */}
            <div style={{
              width: 56, height: 56, borderRadius: 18, flexShrink: 0,
              background: `linear-gradient(135deg, ${C.accent}, #F5C842)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, fontWeight: 700, color: C.accentText,
              boxShadow: `0 4px 16px ${C.accent}44`,
            }}>
              {initialen.toUpperCase()}
            </div>

            {/* Name + Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: -0.5, lineHeight: 1.2, marginBottom: 3 }}>
                {fullName}
              </div>
              {aktivesProfile?.beruf && (
                <div style={{ fontSize: 13, color: C.textMuted }}>
                  {aktivesProfile.beruf}
                </div>
              )}
            </div>
          </div>

          {/* Tags */}
          {(ort || stellen.length > 0) && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {ort && (
                <span style={{ fontSize: 12, color: C.textMuted, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 999, padding: '4px 10px' }}>
                  {ort}
                </span>
              )}
              {stellen.filter(s => !istEntwurf(s)).length > 0 && (
                <span style={{ fontSize: 12, color: C.textMuted, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 999, padding: '4px 10px' }}>
                  {stellen.filter(s => !istEntwurf(s)).length} Bewerbungen
                </span>
              )}
            </div>
          )}
        </div>

        {/* ── Completion Card ── */}
        <CompletionCard profile={aktivesProfile} stellen={stellen} />

        {/* ── Tab Bar ── */}
        <div style={{
          display: 'flex', margin: '0 16px 16px',
          background: C.surface, borderRadius: 14, padding: 4,
          border: `1px solid ${C.border}`,
        }}>
          {TABS.map(t => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                style={{
                  flex: 1, padding: '9px 4px',
                  background: active ? '#fff' : 'transparent',
                  border: `1px solid ${active ? C.border : 'transparent'}`,
                  borderRadius: 10, fontFamily: C.body,
                  fontSize: 12, fontWeight: active ? 600 : 400,
                  color: active ? C.text : C.textMuted,
                  cursor: 'pointer',
                  boxShadow: active ? '0 1px 6px rgba(0,0,0,0.07)' : 'none',
                  transition: 'all 0.15s',
                  WebkitTapHighlightColor: 'transparent',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
              >{t.label}</button>
            );
          })}
        </div>

        {/* ── Tab-Inhalte ── */}
        <div style={{ padding: '0 16px 100px' }}>

          {tab === 'cv' && (
            <>
              <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: C.textDim, marginBottom: 12 }}>
                Gespeicherte Lebensläufe
              </div>
              {cvStellen.length === 0 ? (
                <div style={{ fontSize: 14, color: C.textMuted, padding: '20px 0' }}>
                  Noch keine Lebensläufe generiert.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {cvStellen.map(s => (
                    <FileCard
                      key={s.id}
                      stelle={s}
                      starred={s.id === defaultId}
                      onStarToggle={() => setStarredId(s.id === starredId ? null : s.id)}
                      onOpen={() => onStelleOeffnen?.(s)}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {tab === 'anschreiben' && (
            <>
              <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: C.textDim, marginBottom: 12 }}>
                Gespeicherte Anschreiben
              </div>
              {anschreibenStellen.length === 0 ? (
                <div style={{ fontSize: 14, color: C.textMuted, padding: '20px 0' }}>
                  Noch keine Anschreiben generiert.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {anschreibenStellen.map(s => (
                    <div
                      key={s.id}
                      onClick={() => onStelleOeffnen?.(s)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 14,
                        background: C.surface, border: `1px solid ${C.border}`,
                        borderRadius: 16, padding: '14px 16px', cursor: 'pointer',
                      }}
                    >
                      <svg width="20" height="24" viewBox="0 0 20 24" fill="none">
                        <rect x="1" y="1" width="18" height="22" rx="3" fill="rgba(26,56,40,0.12)" stroke="rgba(26,56,40,0.3)" strokeWidth="1.2"/>
                        <path d="M5 8h10M5 12h10M5 16h6" stroke="rgba(26,56,40,0.6)" strokeWidth="1.3" strokeLinecap="round"/>
                      </svg>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          Anschreiben — {s.name || 'Stelle'}.pdf
                        </div>
                        <div style={{ fontSize: 11.5, color: C.textMuted }}>
                          {formatDatum(s.generiert_am || s.erstellt_am)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {(tab === 'dokumente' || tab === 'fotos') && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>{tab === 'fotos' ? '🖼' : '📎'}</div>
              <div style={{ fontSize: 14, color: C.textMuted }}>
                {tab === 'fotos' ? 'Noch keine Fotos hinterlegt.' : 'Noch keine Dokumente hinterlegt.'}
              </div>
            </div>
          )}

        </div>
      </div>


    </div>
  );
}
